import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { ApiErrorService } from '../services/api-error.service';
import { SKIP_AUTH_REFRESH } from './auth-context';

let refreshRequest$: Observable<string> | undefined;

export const RefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthenticationService);
  const router = inject(Router);
  const errors = inject(ApiErrorService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        errors.show(error.error?.message || 'You do not have permission to perform this action.');
        return throwError(() => error);
      }

      if (error.status !== 401 || shouldNotRefresh(req.url, req.context.get(SKIP_AUTH_REFRESH))) {
        return throwError(() => error);
      }

      const refreshToken = auth.getRefreshToken();
      if (!refreshToken) {
        auth.signOut(router.url);
        return throwError(() => error);
      }

      if (!refreshRequest$) {
        refreshRequest$ = auth.refreshAccessToken().pipe(
          catchError((refreshError) => {
            auth.signOut(router.url);
            return throwError(() => refreshError);
          }),
          finalize(() => { refreshRequest$ = undefined; }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      }

      return refreshRequest$.pipe(
        switchMap((accessToken) => next(req.clone({
          setHeaders: { Authorization: `Bearer ${accessToken}` },
        }))),
      );
    }),
  );
};

function shouldNotRefresh(url: string, skipRefresh: boolean): boolean {
  return skipRefresh || /\/authentication\/(login|refresh|register|external-login)/.test(url);
}
